import React, { useEffect, useMemo, useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { pt } from 'date-fns/locale';

import { Alerta } from '../../components/alerta';
import DayDatePicker from '../../components/dayDatePicker';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import {
    formatIsoDate,
    monthLongNamesPtBr
} from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import { parseNumericText } from '../../utils/numberUtils';

const emptyConsideracao = () => ({ title: '', body: '' });
const emptyGeracao = () => ({
    actualMwh: '',
    guaranteedMwh: '',
    measuredGeneration: '',
    compensationMwh: ''
});
const emptyDistribuicao = () => ({
    distributionPayment: '',
    forecast: ''
});
const emptyDre = () => ({
    consorcioFaturadoMwh: '',
    tarifa: '',
    faturamento: '',
    tusdUsinas: '',
    retencao: '',
    faturamentoSpe: '',
    impostosSobreVendasEServicos: '',
    depreciacoesEAmortizacoes: '',
    manutencoesDeGeradores: '',
    seguros: '',
    aluguelDeTerrenos: '',
    despesasOperacionais: '',
    despesasFinanceiras: '',
    receitasFinanceiras: '',
    resultadoContabil: '',
    resultadoOperacional: ''
});
const toNumber = (value) => parseNumericText(value, NaN);
const DRE_FIELDS = [
    { label: 'Consórcio faturado', key: 'consorcioFaturadoMwh' },
    { label: 'Tarifa', key: 'tarifa' },
    { label: 'Faturamento', key: 'faturamento' },
    { label: 'TUSD usinas', key: 'tusdUsinas' },
    { label: 'Retenção', key: 'retencao' },
    { label: 'Faturamento SPE', key: 'faturamentoSpe' },
    { label: 'Impostos sobre vendas e serviços', key: 'impostosSobreVendasEServicos' },
    { label: 'Depreciações e amortizações', key: 'depreciacoesEAmortizacoes' },
    { label: 'Manutenções de geradores', key: 'manutencoesDeGeradores' },
    { label: 'Seguros', key: 'seguros' },
    { label: 'Aluguel de terrenos', key: 'aluguelDeTerrenos' },
    { label: 'Despesas operacionais', key: 'despesasOperacionais' },
    { label: 'Despesas financeiras', key: 'despesasFinanceiras' },
    { label: 'Receitas financeiras', key: 'receitasFinanceiras' },
    { label: 'Resultado contábil', key: 'resultadoContabil' },
    { label: 'Resultado operacional', key: 'resultadoOperacional' }
];

const AtualizarRelatorioInvestidor = () => {
    const [investidores, setInvestidores] = useState([]);
    const [selectedNome, setSelectedNome] = useState('');
    const [mesRelatorioDate, setMesRelatorioDate] = useState(null);
    const [paymentDate, setPaymentDate] = useState(null);
    const [geracao, setGeracao] = useState(emptyGeracao());
    const [distribuicao, setDistribuicao] = useState(emptyDistribuicao());
    const [dre, setDre] = useState(emptyDre());
    const [drePaste, setDrePaste] = useState('');
    const [consideracoes, setConsideracoes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmit, setIsSubmit] = useState(false);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);

    useEffect(() => {
        async function carregarInvestidores () {
            setIsLoading(true);
            const response = await fetchFlask('listInvestidores');

            if (response.status === 'success') {
                setInvestidores(response.message);
            } else {
                setMessage(response.message || 'Nao foi possivel carregar os investidores.');
                setStatus(response.status || 'error');
                setOpenAlerta(true);
            }

            setIsLoading(false);
        }

        carregarInvestidores();
    }, []);

    const investidorSelecionado = useMemo(() => {
        return investidores.find((item) => item.nome === selectedNome) || null;
    }, [investidores, selectedNome]);

    const hasInvalidConsideracoes = useMemo(() => {
        return consideracoes.some((item) => {
            const hasTitle = item.title.trim().length > 0;
            const hasBody = item.body.trim().length > 0;

            return hasTitle !== hasBody;
        });
    }, [consideracoes]);

    const hasAllRequiredFields = useMemo(() => {
        if (!investidorSelecionado || !mesRelatorioDate || !paymentDate) {
            return false;
        }

        const requiredNumericFields = [
            ...Object.values(geracao),
            ...Object.values(distribuicao),
            ...Object.values(dre)
        ];

        return requiredNumericFields.every((value) => String(value).trim() !== '');
    }, [investidorSelecionado, mesRelatorioDate, paymentDate, geracao, distribuicao, dre]);

    const hasInvalidNumericFields = useMemo(() => {
        return [
            ...Object.values(geracao),
            ...Object.values(distribuicao),
            ...Object.values(dre)
        ].some((value) => Number.isNaN(toNumber(value)));
    }, [geracao, distribuicao, dre]);

    function handleAlerta (texto, novoStatus) {
        setMessage(texto);
        setStatus(novoStatus);
        setOpenAlerta(true);
    }

    function resetFormularioDetalhes () {
        setMesRelatorioDate(null);
        setPaymentDate(null);
        setGeracao(emptyGeracao());
        setDistribuicao(emptyDistribuicao());
        setDre(emptyDre());
        setDrePaste('');
        setConsideracoes([]);
    }

    function addConsideracao () {
        setConsideracoes((prev) => [...prev, emptyConsideracao()]);
    }

    function updateConsideracao (index, field, value) {
        setConsideracoes((prev) => prev.map((item, currentIndex) =>
            currentIndex === index ? { ...item, [field]: value } : item
        ));
    }

    function removeConsideracao (index) {
        setConsideracoes((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
    }

    function formatMesRelatorio (date) {
        const month = monthLongNamesPtBr[date.getMonth()].toLowerCase();
        const year = String(date.getFullYear()).slice(-2);

        return `${month}/${year}`;
    }

    function parseDrePaste (text) {
        const values = text
            .split(/\r?\n/)
            .flatMap((row) => row.split('\t'))
            .map((value) => value.trim())
            .filter(Boolean);

        if (values.length === 0) {
            return;
        }

        const nextDre = { ...dre };
        DRE_FIELDS.forEach((field, index) => {
            nextDre[field.key] = values[index] || '';
        });

        setDre(nextDre);
    }

    function handleGeracaoChange (field, value) {
        setGeracao((prev) => ({ ...prev, [field]: value }));
    }

    function handleDistribuicaoChange (field, value) {
        setDistribuicao((prev) => ({ ...prev, [field]: value }));
    }

    function handleDreChange (field, value) {
        setDre((prev) => ({ ...prev, [field]: value }));
    }

    async function submitAtualizarRelatorioInvestidor (e) {
        e.preventDefault();

        if (!hasAllRequiredFields) {
            return;
        }

        if (hasInvalidNumericFields) {
            handleAlerta('Existem valores numericos invalidos no formulario.', 'error');
            return;
        }

        if (hasInvalidConsideracoes) {
            handleAlerta('Preencha titulo e texto de cada consideracao adicionada.', 'error');
            return;
        }

        setIsSubmit(true);

        const mesRelatorio = formatMesRelatorio(mesRelatorioDate);
        const dadosRelatorio = {
            considerations: consideracoes
                .filter((item) => item.title.trim() && item.body.trim())
                .map((item) => ({
                    title: item.title.trim(),
                    body: item.body.trim()
                })),
            distribution: {
                distributionPayment: toNumber(distribuicao.distributionPayment),
                forecast: toNumber(distribuicao.forecast),
                paymentDate: formatIsoDate(paymentDate)
            },
            dre: Object.fromEntries(
                Object.entries(dre).map(([key, value]) => [key, toNumber(value)])
            ),
            generation: {
                actualMwh: toNumber(geracao.actualMwh),
                guaranteedMwh: toNumber(geracao.guaranteedMwh),
                measuredGeneration: toNumber(geracao.measuredGeneration),
                compensationMwh: toNumber(geracao.compensationMwh)
            }
        };

        const body = JSON.stringify({
            awsId: investidorSelecionado.awsID,
            mesRelatorio,
            dadosRelatorio
        });

        const response = await fetchFlask('atualizarRelatorioInvestidor', body);
        handleAlerta(response.message, response.status);

        if (response.status === 'success') {
            setSelectedNome('');
            resetFormularioDetalhes();
        }

        setIsSubmit(false);
    }

    return (
        <Box className='mainBox'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />

            <PageHeader>Atualizar Relatorio SPE</PageHeader>

            <Box sx={{ width: '100%', maxWidth: 1440, mx: 'auto' }}>
                <form onSubmit={submitAtualizarRelatorioInvestidor}>
                    <Stack spacing={3}>
                        <FormControl fullWidth>
                            <InputLabel id='investidor-select-label'>Investidor</InputLabel>
                            <Select
                                labelId='investidor-select-label'
                                value={selectedNome}
                                label='Investidor'
                                disabled={isLoading || investidores.length === 0}
                                onChange={(e) => {
                                    setSelectedNome(e.target.value);
                                    resetFormularioDetalhes();
                                }}
                            >
                                {investidores.map((investidor) => (
                                    <MenuItem key={investidor.awsID} value={investidor.nome}>
                                        {investidor.nome}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {isLoading && (
                            <Typography color='text.secondary'>
                                Carregando investidores...
                            </Typography>
                        )}

                        {!isLoading && investidores.length === 0 && (
                            <Typography color='text.secondary'>
                                Nenhum investidor disponivel para selecao.
                            </Typography>
                        )}

                        {investidorSelecionado && (
                            <>
                                <LocalizationProvider adapterLocale={pt} dateAdapter={AdapterDateFns}>
                                    <Box sx={{ maxWidth: { xs: '100%', md: 360 } }}>
                                        <DatePicker
                                            label='Mes do relatorio'
                                            views={['month', 'year']}
                                            openTo='month'
                                            minDate={new Date('2026-01-01')}
                                            maxDate={new Date()}
                                            value={mesRelatorioDate}
                                            onChange={(value) => setMesRelatorioDate(value)}
                                            slotProps={{
                                                textField: {
                                                    required: true,
                                                    fullWidth: true
                                                }
                                            }}
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                                            gap: 2.5,
                                            width: '100%',
                                            alignItems: 'start'
                                        }}
                                    >
                                        <Paper
                                            variant='outlined'
                                            sx={{
                                                p: 2.5,
                                                borderRadius: 3,
                                                minWidth: 0,
                                                background: 'linear-gradient(180deg, #f7fbff 0%, #ffffff 100%)',
                                                borderColor: '#cfe1f8'
                                            }}
                                        >
                                            <Stack spacing={2}>
                                                <Typography variant='h6'>Geração</Typography>

                                                <TextField
                                                    required
                                                    label='Geração Real (MWh)'
                                                    value={geracao.actualMwh}
                                                    onChange={(e) => handleGeracaoChange('actualMwh', e.target.value)}
                                                    inputProps={{ inputMode: 'decimal' }}
                                                    fullWidth
                                                />

                                                <TextField
                                                    required
                                                    label='Energia Injetada (MWh)'
                                                    value={geracao.measuredGeneration}
                                                    onChange={(e) => handleGeracaoChange('measuredGeneration', e.target.value)}
                                                    inputProps={{ inputMode: 'decimal' }}
                                                    fullWidth
                                                />

                                                <TextField
                                                    required
                                                    label='Geracao Garantida (MWh)'
                                                    value={geracao.guaranteedMwh}
                                                    onChange={(e) => handleGeracaoChange('guaranteedMwh', e.target.value)}
                                                    inputProps={{ inputMode: 'decimal' }}
                                                    fullWidth
                                                />

                                                <TextField
                                                    required
                                                    label='Compensação (MWh)'
                                                    value={geracao.compensationMwh}
                                                    onChange={(e) => handleGeracaoChange('compensationMwh', e.target.value)}
                                                    inputProps={{ inputMode: 'decimal' }}
                                                    fullWidth
                                                />
                                            </Stack>
                                        </Paper>

                                        <Paper
                                            variant='outlined'
                                            sx={{
                                                p: 2.5,
                                                borderRadius: 3,
                                                minWidth: 0,
                                                background: 'linear-gradient(180deg, #fbfbff 0%, #ffffff 100%)',
                                                borderColor: '#dad8f6'
                                            }}
                                        >
                                            <Stack spacing={2}>
                                                <Typography variant='h6'>Distribuicao</Typography>

                                                <TextField
                                                    required
                                                    label='Pagamento de Distribuicao (R$)'
                                                    value={distribuicao.distributionPayment}
                                                    onChange={(e) => handleDistribuicaoChange('distributionPayment', e.target.value)}
                                                    inputProps={{ inputMode: 'decimal' }}
                                                    fullWidth
                                                />

                                                <TextField
                                                    required
                                                    label='Distribuicao Prevista (R$)'
                                                    value={distribuicao.forecast}
                                                    onChange={(e) => handleDistribuicaoChange('forecast', e.target.value)}
                                                    inputProps={{ inputMode: 'decimal' }}
                                                    fullWidth
                                                />

                                                <DayDatePicker
                                                    label='Data de Pagamento'
                                                    minDate={new Date('2026-01-01')}
                                                    maxDate={new Date()}
                                                    value={paymentDate}
                                                    onChange={setPaymentDate}
                                                    required
                                                    fullWidth
                                                />
                                            </Stack>
                                        </Paper>

                                        <Paper
                                            variant='outlined'
                                            sx={{
                                                p: 2.5,
                                                borderRadius: 3,
                                                minWidth: 0,
                                                background: 'linear-gradient(180deg, #fffaf4 0%, #ffffff 100%)',
                                                borderColor: '#f0dfbf'
                                            }}
                                        >
                                            <Stack spacing={2}>
                                                <Typography variant='h6'>DRE</Typography>

                                                <TextField
                                                    label='Colar valores do Excel'
                                                    placeholder={'Cole os 16 valores na ordem esperada, um por linha ou em coluna.'}
                                                    value={drePaste}
                                                    onChange={(e) => setDrePaste(e.target.value)}
                                                    onPaste={(e) => {
                                                        const text = e.clipboardData.getData('text');
                                                        setDrePaste(text);
                                                        parseDrePaste(text);
                                                    }}
                                                    multiline
                                                    minRows={5}
                                                    fullWidth
                                                    helperText='Os valores serao aplicados na ordem exibida abaixo.'
                                                />

                                                {DRE_FIELDS.map((field) => (
                                                    <TextField
                                                        key={field.key}
                                                        required
                                                        label={field.label}
                                                        value={dre[field.key]}
                                                        onChange={(e) => handleDreChange(field.key, e.target.value)}
                                                        inputProps={{ inputMode: 'decimal' }}
                                                        fullWidth
                                                    />
                                                ))}
                                            </Stack>
                                        </Paper>
                                    </Box>
                                </LocalizationProvider>

                                <Stack spacing={2}>
                                    <Box>
                                        <Button
                                            type='button'
                                            variant='contained'
                                            startIcon={<AddRoundedIcon />}
                                            onClick={addConsideracao}
                                        >
                                            Adicionar Consideração
                                        </Button>
                                    </Box>

                                    {consideracoes.map((consideracao, index) => (
                                        <Paper key={index} variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
                                            <Stack spacing={2}>
                                                <Box display='flex' justifyContent='space-between' alignItems='center'>
                                                    <Typography variant='subtitle1'>
                                                        Consideração {index + 1}
                                                    </Typography>
                                                    <Button
                                                        type='button'
                                                        color='error'
                                                        startIcon={<DeleteRoundedIcon />}
                                                        onClick={() => removeConsideracao(index)}
                                                    >
                                                        Remover
                                                    </Button>
                                                </Box>

                                                <TextField
                                                    label='Título'
                                                    value={consideracao.title}
                                                    onChange={(e) => updateConsideracao(index, 'title', e.target.value)}
                                                    fullWidth
                                                />

                                                <TextField
                                                    label='Texto da consideração'
                                                    value={consideracao.body}
                                                    onChange={(e) => updateConsideracao(index, 'body', e.target.value)}
                                                    fullWidth
                                                    multiline
                                                    minRows={4}
                                                />
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>

                                <Box>
                                    <FormControl>
                                        <SubmitButton isSubmit={isSubmit} startIcon={<SaveRoundedIcon />}>
                                            Atualizar Relatório
                                        </SubmitButton>
                                    </FormControl>
                                </Box>
                            </>
                        )}
                    </Stack>
                </form>
            </Box>
        </Box>
    );
};

export default AtualizarRelatorioInvestidor;
